import React, {useMemo, useRef, useEffect} from 'react';
import * as css from './ProcessPopup.css';
import {StepperProps} from './ProcessPopup.types';
import StepItem from './StepItem';

function Stepper({steps, currentStep, onStepClick, validateStep, dataCyName}: StepperProps) {
    const maxStepVisitedRef = useRef<number>(currentStep);

    useEffect(() => {
        if(currentStep > maxStepVisitedRef.current) {
            maxStepVisitedRef.current = currentStep;
        }
    }, [currentStep]);

    const handleStepClick = async (stepIndex: number): Promise<void> => {
        if(!onStepClick) {
            return;
        }
        if(validateStep && stepIndex > currentStep) {
            const isValid = await validateStep(currentStep);
            if(!isValid) {
                return;
            }
        }
        onStepClick(stepIndex);
    };

    const segments = useMemo(() =>
        steps.map((_, index) => {
            const isBeforeCurrent = index < currentStep;
            const isCurrent = index === currentStep;
            const isJustBeforeCurrent = index === currentStep - 1;
            const className = isJustBeforeCurrent
                ? 'toCurrent'
                : isBeforeCurrent
                    ? 'complete'
                    : isCurrent
                        ? 'current'
                        : '';

            return {className, key: `segment-${index}`};
        }),[steps.length, currentStep]);

    const stepItems = useMemo(() =>
        steps.map((step, index) => {
            const isComplete = step.completed || index < currentStep;
            const isCurrent = index === currentStep;
            const isClickable = !!onStepClick && index !== currentStep;
            
            return {
                step,
                index,
                isComplete,
                isCurrent,
                isClickable,
                key: `step-${index}`
            };
        }),[steps, currentStep, onStepClick]);

    return (
        <css.stepper>
            <css.stepperLine>
                {segments.map(({className, key}) => (
                    <css.stepSegment key={key} className={className} />
                ))}
            </css.stepperLine>
            <css.steps>
                {stepItems.map(({step, index, isComplete, isCurrent, isClickable, key}) => (
                    <StepItem
                        key={key}
                        step={step}
                        index={index}
                        isComplete={isComplete}
                        isCurrent={isCurrent}
                        isClickable={isClickable}
                        onClick={handleStepClick}
                        dataCyName={dataCyName}
                    />
                ))}
            </css.steps>
        </css.stepper>
    );
}

export default Stepper;
